import { useEffect, useMemo, useState } from 'react';
import { KeyRound, Plus, RefreshCw, UserCheck, UserX } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { employeePortalUsersAPI } from '../services/api';
import { filterBySearch } from '../utils/search';

const DEFAULT_PASSWORD = 'Admin@123';

export default function EmployeePortalUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [form, setForm] = useState({ employee_id: '', email: '', password: DEFAULT_PASSWORD });
  const [resetPassword, setResetPassword] = useState(DEFAULT_PASSWORD);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const filteredRows = useMemo(
    () => filterBySearch(rows, search, [
      'full_name',
      'employee_code',
      'work_email',
      'portal_email',
      'enrollment_status',
    ]),
    [rows, search]
  );

  const load = () => {
    setLoading(true);
    employeePortalUsersAPI.list()
      .then(({ data }) => setRows(data))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load portal users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const withoutLogin = rows.filter((r) => !r.user_id && r.enrollment_status === 'enrolled');

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      const { data } = await employeePortalUsersAPI.create({
        employee_id: parseInt(form.employee_id, 10),
        email: form.email || undefined,
        password: form.password || DEFAULT_PASSWORD,
      });
      setMessage(`Login created for ${data.email}. Temporary password: ${data.temporaryPassword}`);
      setShowCreate(false);
      setForm({ employee_id: '', email: '', password: DEFAULT_PASSWORD });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create portal login');
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      const { data } = await employeePortalUsersAPI.resetPassword(resetUserId, resetPassword);
      setMessage(`Password reset. New temporary password: ${data.temporaryPassword}`);
      setResetUserId(null);
      setResetPassword(DEFAULT_PASSWORD);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password');
    }
  };

  const toggleActive = async (userId, isActive) => {
    setError(null);
    setMessage(null);
    try {
      await employeePortalUsersAPI.setStatus(userId, !isActive);
      setMessage(`Portal login ${!isActive ? 'activated' : 'deactivated'}`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const openCreateFor = (row) => {
    setForm({
      employee_id: String(row.employee_id),
      email: row.work_email || '',
      password: DEFAULT_PASSWORD,
    });
    setShowCreate(true);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Employee Portal Logins</h1>
          <p className="text-slate-500 mt-1">Create and manage employee self-service accounts</p>
        </div>
        {withoutLogin.length > 0 && (
          <button type="button" onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2 self-start">
            <Plus className="w-4 h-4" />
            Create Portal Login
          </button>
        )}
      </div>

      {message && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">{message}</div>
      )}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {showCreate && (
        <form onSubmit={handleCreate} className="card space-y-4">
          <h3 className="font-semibold text-lg">Create Portal Login</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Employee *</label>
              <select
                className="input-field"
                required
                value={form.employee_id}
                onChange={(e) => {
                  const emp = withoutLogin.find((r) => String(r.employee_id) === e.target.value);
                  setForm({
                    ...form,
                    employee_id: e.target.value,
                    email: emp?.work_email || '',
                  });
                }}
              >
                <option value="">Select enrolled employee</option>
                {withoutLogin.map((r) => (
                  <option key={r.employee_id} value={r.employee_id}>
                    {r.full_name} ({r.employee_code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Login Email *</label>
              <input
                type="email"
                className="input-field"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Temporary Password *</label>
              <input
                type="text"
                className="input-field"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary">Create Login</button>
            <button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </form>
      )}

      {resetUserId && (
        <form onSubmit={handleReset} className="card space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Reset Password
          </h3>
          <div className="max-w-sm">
            <label className="label">New Temporary Password</label>
            <input
              type="text"
              className="input-field"
              required
              minLength={8}
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary">Reset Password</button>
            <button type="button" className="btn-secondary" onClick={() => setResetUserId(null)}>Cancel</button>
          </div>
        </form>
      )}

      <SearchInput
        className="max-w-md"
        placeholder="Search by name, code, email…"
        value={search}
        onChange={setSearch}
      />

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-slate-500">
              <th className="pb-3 pr-4 font-medium">Employee</th>
              <th className="pb-3 pr-4 font-medium hidden sm:table-cell">Work Email</th>
              <th className="pb-3 pr-4 font-medium">Portal Email</th>
              <th className="pb-3 pr-4 font-medium">Status</th>
              <th className="pb-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr key={row.employee_id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 pr-4">
                  <div className="font-medium text-slate-900">{row.full_name}</div>
                  <div className="text-xs text-slate-400">{row.employee_code}</div>
                </td>
                <td className="py-3 pr-4 hidden sm:table-cell text-slate-600">{row.work_email}</td>
                <td className="py-3 pr-4 text-slate-600">{row.portal_email || '—'}</td>
                <td className="py-3 pr-4">
                  {!row.user_id ? (
                    <span className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-full">No login</span>
                  ) : row.portal_active ? (
                    <span className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">Active</span>
                  ) : (
                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">Disabled</span>
                  )}
                </td>
                <td className="py-3">
                  <div className="flex flex-wrap gap-2">
                    {!row.user_id && row.enrollment_status === 'enrolled' && (
                      <button type="button" onClick={() => openCreateFor(row)} className="text-primary-600 hover:underline text-xs">
                        Create login
                      </button>
                    )}
                    {row.user_id && (
                      <>
                        <button
                          type="button"
                          onClick={() => setResetUserId(row.user_id)}
                          className="text-primary-600 hover:underline text-xs flex items-center gap-1"
                        >
                          <RefreshCw className="w-3 h-3" />
                          Reset password
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(row.user_id, row.portal_active)}
                          className="text-slate-600 hover:underline text-xs flex items-center gap-1"
                        >
                          {row.portal_active ? (
                            <><UserX className="w-3 h-3" /> Disable</>
                          ) : (
                            <><UserCheck className="w-3 h-3" /> Enable</>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="text-center py-8 text-slate-500">No enrolled employees found.</p>
        )}
        {rows.length > 0 && filteredRows.length === 0 && (
          <p className="text-center py-8 text-slate-500">No employees match your search.</p>
        )}
      </div>
    </div>
  );
}
